
import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, User, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { CreditsDisplay } from "@/components/CreditsDisplay";

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { theme, setTheme } = useUI();
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  const getUserPlan = () => {
    return profile?.subscription?.plan || "free";
  };

  const userPlan = getUserPlan();

  const handleLogoClick = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  return (
    <nav
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled
          ? "bg-background/80 backdrop-blur-sm border-b"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <div 
          onClick={handleLogoClick}
          className="flex items-center cursor-pointer"
        >
          <img 
            src={theme === "light" 
              ? "/lovable-uploads/58aa0bd4-0cb6-4e93-8bb2-e2fcf729ba9f.png" 
              : "/lovable-uploads/612cf211-c5c9-425d-84c0-109a11ccbfc7.png"}
            alt="Vitatee" 
            className="h-8"
          />
        </div>

        <div className="hidden md:flex items-center space-x-1">
          {!user && (
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm ${
                location.pathname === "/"
                  ? "text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground transition-colors"
              }`}
            >
              Home
            </Link>
          )}
          
          {!user && (
            <Link
              to="/pricing"
              className={`px-3 py-2 rounded-md text-sm ${
                location.pathname === "/pricing"
                  ? "text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground transition-colors"
              }`}
            >
              Pricing
            </Link>
          )}
          
          {!user && (
            <Link
              to="/blog"
              className={`px-3 py-2 rounded-md text-sm ${
                location.pathname.startsWith("/blog")
                  ? "text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground transition-colors"
              }`}
            >
              Blog
            </Link>
          )}
          
          {user && (
            <>
              <Link
                to="/dashboard"
                className={`px-3 py-2 rounded-md text-sm ${
                  location.pathname === "/dashboard"
                    ? "text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground transition-colors"
                }`}
              >
                Dashboard
              </Link>
              <Link
                to="/vita"
                className={`px-3 py-2 rounded-md text-sm ${
                  location.pathname === "/vita"
                    ? "text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground transition-colors"
                }`}
              >
                Vita Chat
              </Link>
              <Link
                to="/profile"
                className={`px-3 py-2 rounded-md text-sm ${
                  location.pathname === "/profile"
                    ? "text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground transition-colors"
                }`}
              >
                Profile
              </Link>
            </>
          )}
          
          <Button
            variant="outline"
            size="sm"
            className="ml-4"
            onClick={toggleTheme}
          >
            {theme === "light" ? "Dark" : "Light"} Mode
          </Button>
          
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="ml-2">
                  <User className="h-4 w-4 mr-1" />
                  Account
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  Plan: <span className="font-semibold capitalize">{userPlan}</span>
                </DropdownMenuLabel>
                {userPlan === "free" && (
                  <DropdownMenuItem onClick={() => navigate("/pricing")} className="text-primary">
                    <ArrowUp className="h-4 w-4 mr-2" />
                    Upgrade Plan
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")}>
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard")}>
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/vita")}>
                  Vita Chat
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <CreditsDisplay />
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/blog")}>
                  Blog
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/terms")}>
                  Terms
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/privacy")}>
                  Privacy
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => signOut()}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" className="ml-2">
              <Link to="/auth">Sign In</Link>
            </Button>
          )}
        </div>

        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={toggleMenu}>
            {isMenuOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </div>

      {isMenuOpen && (
        <div className="md:hidden bg-background border-b">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {!user && (
              <Link
                to="/"
                className={`block px-3 py-2 rounded-md text-base ${
                  location.pathname === "/"
                    ? "text-primary font-medium"
                    : "text-muted-foreground"
                }`}
                onClick={toggleMenu}
              >
                Home
              </Link>
            )}
            
            {!user && (
              <Link
                to="/pricing"
                className={`block px-3 py-2 rounded-md text-base ${
                  location.pathname === "/pricing"
                    ? "text-primary font-medium"
                    : "text-muted-foreground"
                }`}
                onClick={toggleMenu}
              >
                Pricing
              </Link>
            )}
            
            {!user && (
              <Link
                to="/blog"
                className={`block px-3 py-2 rounded-md text-base ${
                  location.pathname.startsWith("/blog")
                    ? "text-primary font-medium"
                    : "text-muted-foreground"
                }`}
                onClick={toggleMenu}
              >
                Blog
              </Link>
            )}
            
            {user && (
              <>
                <Link
                  to="/dashboard"
                  className={`block px-3 py-2 rounded-md text-base ${
                    location.pathname === "/dashboard"
                      ? "text-primary font-medium"
                      : "text-muted-foreground"
                  }`}
                  onClick={toggleMenu}
                >
                  Dashboard
                </Link>
                <Link
                  to="/vita"
                  className={`block px-3 py-2 rounded-md text-base ${
                    location.pathname === "/vita"
                      ? "text-primary font-medium"
                      : "text-muted-foreground"
                  }`}
                  onClick={toggleMenu}
                >
                  Vita Chat
                </Link>
                <Link
                  to="/profile"
                  className={`block px-3 py-2 rounded-md text-base ${
                    location.pathname === "/profile"
                      ? "text-primary font-medium"
                      : "text-muted-foreground"
                  }`}
                  onClick={toggleMenu}
                >
                  Profile
                </Link>
              </>
            )}
            
            <div className="flex flex-col space-y-2 pt-2">
              <Button variant="outline" onClick={toggleTheme}>
                {theme === "light" ? "Dark" : "Light"} Mode
              </Button>
              
              {user ? (
                <>
                  {userPlan === "free" && (
                    <Button variant="default" onClick={() => {
                      navigate("/pricing");
                      toggleMenu();
                    }}>
                      <ArrowUp className="h-4 w-4 mr-2" />
                      Upgrade Plan
                    </Button>
                  )}
                  <Button variant="destructive" onClick={() => signOut()}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign out
                  </Button>
                </>
              ) : (
                <Button asChild onClick={toggleMenu}>
                  <Link to="/auth">Sign In</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
